import { SystemUser, UserFormData, UserActivity, UserStatistics } from '../types/user';
import { get, post, put, del } from '../utils/httpClient';

// --- Types ---

export interface UserSearchParams {
  search?: string;
  role?: string;
  organization?: string;
  is_active?: boolean;
  sort_by?: string;
  sort_order?: 'ASC' | 'DESC';
  limit?: number;
  offset?: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  limit: number;
  offset: number;
  total_pages: number;
}

// --- Helpers ---

function buildQueryString(params: UserSearchParams): string {
  const query = new URLSearchParams();
  if (params.search) query.set('search', params.search);
  if (params.role) query.set('role', params.role);
  if (params.organization) query.set('organization', params.organization);
  if (params.is_active !== undefined) query.set('is_active', String(params.is_active));
  if (params.sort_by) query.set('sort_by', params.sort_by);
  if (params.sort_order) query.set('sort_order', params.sort_order);
  if (params.limit) query.set('limit', String(params.limit));
  if (params.offset !== undefined) query.set('offset', String(params.offset));
  const qs = query.toString();
  return qs ? `?${qs}` : '';
}

async function parseErrorMessage(response: Response, fallback: string): Promise<string> {
  try {
    const data = await response.json();
    return data.error || data.message || fallback;
  } catch {
    return fallback;
  }
}

// --- API Functions ---

/** Fetch system users with optional search/filter/pagination */
export async function fetchUsers(params: UserSearchParams = {}): Promise<PaginatedResponse<SystemUser>> {
  const qs = buildQueryString({ limit: 50, ...params });
  const response = await get(`/api/v1/admin/users${qs}`);
  if (!response.ok) {
    const msg = await parseErrorMessage(response, 'Erreur lors de la récupération des utilisateurs');
    throw new Error(msg);
  }
  return response.json();
}

/** Fetch a single user by ID */
export async function fetchUserById(userId: string): Promise<SystemUser> {
  const response = await get(`/api/v1/admin/users/${userId}`);
  if (!response.ok) {
    const msg = await parseErrorMessage(response, "Erreur lors de la récupération de l'utilisateur");
    throw new Error(msg);
  }
  return response.json();
}

/** Create a new system user */
export async function createUser(data: UserFormData): Promise<SystemUser> {
  const response = await post('/api/v1/admin/users', {
    email: data.email,
    password: data.password || 'TempPass123!',
    full_name: data.full_name,
    role: data.role,
    organization: data.organization,
  });
  if (!response.ok) {
    const msg = await parseErrorMessage(response, "Erreur lors de la création de l'utilisateur");
    throw new Error(msg);
  }
  return response.json();
}

/** Update an existing system user */
export async function updateUser(id: string, data: Partial<UserFormData>): Promise<SystemUser> {
  const response = await put(`/api/v1/admin/users/${id}`, {
    full_name: data.full_name,
    organization: data.organization,
  });
  if (!response.ok) {
    const msg = await parseErrorMessage(response, "Erreur lors de la mise à jour de l'utilisateur");
    throw new Error(msg);
  }
  return response.json();
}

/** Delete a system user */
export async function deleteUser(id: string): Promise<void> {
  const response = await del(`/api/v1/admin/users/${id}`);
  if (!response.ok) {
    const msg = await parseErrorMessage(response, "Erreur lors de la suppression de l'utilisateur");
    throw new Error(msg);
  }
}

/** Activate a user account */
export async function activateUser(id: string): Promise<void> {
  const response = await post(`/api/v1/admin/users/${id}/activate`);
  if (!response.ok) {
    const msg = await parseErrorMessage(response, "Erreur lors de l'activation de l'utilisateur");
    throw new Error(msg);
  }
}

/** Deactivate a user account */
export async function deactivateUser(id: string): Promise<void> {
  const response = await post(`/api/v1/admin/users/${id}/deactivate`);
  if (!response.ok) {
    const msg = await parseErrorMessage(response, "Erreur lors de la désactivation de l'utilisateur");
    throw new Error(msg);
  }
}

/** Reset a user's password (admin-initiated) */
export async function resetUserPassword(id: string): Promise<{ message: string; temporary_password: string }> {
  const response = await post(`/api/v1/admin/users/${id}/reset-password`);
  if (!response.ok) {
    const msg = await parseErrorMessage(response, 'Erreur lors de la réinitialisation du mot de passe');
    throw new Error(msg);
  }
  return response.json();
}

/** Fetch user activity log */
export async function fetchUserActivity(userId: string, limit: number = 50): Promise<UserActivity[]> {
  const response = await get(`/api/v1/admin/users/${userId}/activity?limit=${limit}`);
  if (!response.ok) {
    const msg = await parseErrorMessage(response, "Erreur lors de la récupération de l'historique d'activité");
    throw new Error(msg);
  }
  return response.json();
}

/** Fetch user statistics */
export async function fetchUserStatistics(): Promise<UserStatistics> {
  const response = await get('/api/v1/admin/users/statistics');
  if (!response.ok) {
    const msg = await parseErrorMessage(response, 'Erreur lors de la récupération des statistiques');
    throw new Error(msg);
  }
  return response.json();
}
